/**
 * All widget CSS, injected into the Shadow DOM as a single <style>. Because it
 * lives in a shadow root, none of these rules leak into — or are overridden by
 * — the store's theme. The only themeable knob is --wc-accent.
 */
export function css(accent: string, headerBg: string, headerFg: string): string {
  return `
  :host { all: initial; }
  * { box-sizing: border-box; }

  :host, .wc-root { margin: 0; padding: 0; }

  /* Header: logo + centered title above the matches. */
  .wc-header {
    --accent: ${accent};
    font-family: ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    background: ${headerBg};
    color: ${headerFg};
    padding: 7px 14px;
    border-bottom: 2px solid var(--accent);
  }
  .wc-logo { height: 36px; width: auto; display: block; flex: 0 0 auto; }
  .wc-logo-emoji { font-size: 26px; line-height: 1; }
  .wc-title {
    font-size: 14px;
    font-weight: 800;
    letter-spacing: .9px;
    text-transform: uppercase;
  }

  .wc-bar {
    --accent: ${accent};
    --live: #ff3b30;     /* AO VIVO  */
    --upcoming: #3b82f6; /* PRÓXIMA  */
    --ft: #8a97a5;       /* TERMINADA */
    font-family: ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
    background: #0b0f14;
    color: #e7edf3;
    border-bottom: 2px solid var(--accent);
    width: 100%;
    position: relative;
    display: flex;
    align-items: stretch;
    height: 56px;
    overflow: hidden;
  }

  /* Pinned area: live/HT cards that stay fixed (don't rotate). */
  .wc-pinned {
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 0 0 auto;
    padding: 8px 12px;
    border-right: 1px solid #2a3540;
  }
  .wc-pinned:empty { display: none; } /* no live match -> no pinned area/divider */

  /* Wrapper that scopes the scroller + its edge arrows. */
  .wc-scroll-wrap {
    position: relative;
    flex: 1 1 auto;
    min-width: 0;
    display: flex;
    overflow: hidden;
  }

  .wc-scroller {
    display: flex;
    gap: 8px;
    align-items: center;
    overflow-x: auto;
    /* No CSS smooth-scroll here: it fights the per-frame auto-scroll. Arrow
       buttons opt into smooth explicitly via scrollBy({behavior:'smooth'}). */
    padding: 8px 40px;
    width: 100%;
    scrollbar-width: none;
    cursor: grab;
    -webkit-overflow-scrolling: touch;
  }
  .wc-scroller::-webkit-scrollbar { display: none; }
  .wc-scroller.wc-dragging { cursor: grabbing; }

  .wc-card {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    gap: 10px;
    background: #141b23;
    border: 1px solid #222c37;
    border-radius: 10px;
    padding: 6px 12px;
    height: 40px;
    white-space: nowrap;
    user-select: none;
    transition: border-color .15s ease, background .15s ease;
  }
  .wc-card[data-clickable] { cursor: pointer; }
  /* One distinct color per state, shown as a left stripe + border tint. */
  .wc-card.wc-live {
    background: #211619;
    border-color: var(--live);
    border-left: 3px solid var(--live);
  }
  .wc-card.wc-upcoming {
    border-color: color-mix(in srgb, var(--upcoming) 60%, #222c37);
    border-left: 3px solid var(--upcoming);
  }
  .wc-card.wc-ft {
    border-left: 3px solid var(--ft);
  }
  .wc-card[data-clickable]:hover { filter: brightness(1.15); }

  .wc-side {
    display: flex;
    align-items: center;
    gap: 6px;
    font-weight: 600;
    font-size: 13px;
  }
  .wc-flag {
    width: 20px;
    height: 14px;
    object-fit: cover;
    border-radius: 2px;
    background: #2a3540;
    flex: 0 0 auto;
  }
  .wc-code { letter-spacing: .3px; }

  .wc-score {
    font-variant-numeric: tabular-nums;
    font-weight: 700;
    font-size: 14px;
    padding: 0 8px;
    color: #fff;
  }

  .wc-state { display: flex; align-items: center; gap: 5px; font-size: 11px; font-weight: 700; }
  .wc-min { color: var(--live); font-variant-numeric: tabular-nums; }
  .wc-htlabel { color: var(--live); letter-spacing: .5px; }
  .wc-ft { color: var(--ft); letter-spacing: .5px; }
  .wc-kickoff { color: var(--upcoming); font-variant-numeric: tabular-nums; white-space: nowrap; }

  .wc-dot {
    width: 8px; height: 8px; border-radius: 50%;
    background: var(--live);
    box-shadow: 0 0 0 0 rgba(255,59,48,.6);
    animation: wc-pulse 1.4s infinite;
  }
  @keyframes wc-pulse {
    0% { box-shadow: 0 0 0 0 rgba(255,59,48,.6); }
    70% { box-shadow: 0 0 0 7px rgba(255,59,48,0); }
    100% { box-shadow: 0 0 0 0 rgba(255,59,48,0); }
  }

  .wc-stage { font-size: 10px; color: #6b7785; margin-right: 2px; max-width: 90px; overflow: hidden; text-overflow: ellipsis; }

  .wc-arrow {
    position: absolute;
    top: 0; bottom: 0;
    width: 36px;
    border: none;
    background: linear-gradient(90deg, #0b0f14 30%, transparent);
    color: #e7edf3;
    cursor: pointer;
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    opacity: .9;
  }
  .wc-arrow:hover { opacity: 1; }
  .wc-arrow.wc-prev { left: 0; }
  .wc-arrow.wc-next { right: 0; background: linear-gradient(270deg, #0b0f14 30%, transparent); }
  .wc-arrow[hidden] { display: none; }

  .wc-updating {
    position: absolute;
    top: 6px; right: 44px;
    width: 6px; height: 6px; border-radius: 50%;
    background: var(--accent);
    opacity: 0;
    transition: opacity .2s ease;
  }
  .wc-bar.wc-is-updating .wc-updating { opacity: .9; }

  .wc-root { position: relative; }

  /* Detail dropdown — rendered in a body-level portal (position: fixed via the
     overlay element), limited width, floats below the clicked card. */
  .wc-detail {
    display: none;
    width: 380px;
    max-width: calc(100vw - 16px);
    padding: 12px 14px;
    background: #0e141b;
    color: #cdd6df;
    font-size: 13px;
    border: 1px solid #2a3540;
    border-radius: 12px;
    box-shadow: 0 12px 28px rgba(0, 0, 0, .5);
    flex-direction: column;
    gap: 10px;
  }
  .wc-detail.wc-open { display: flex; }
  .wc-detail-head { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
  .wc-detail-teams { font-weight: 700; color: #fff; font-size: 15px; }
  .wc-detail-status { color: #8a97a5; font-size: 12px; }

  .wc-goals { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 7px; max-width: 480px; }
  .wc-goal { display: flex; align-items: center; gap: 9px; }
  .wc-goal.wc-goal-away { flex-direction: row-reverse; }
  .wc-goal-min { min-width: 34px; color: #8a97a5; font-variant-numeric: tabular-nums; }
  .wc-goal-pill {
    display: inline-flex; align-items: center; gap: 5px;
    background: #5159c9; color: #fff;
    border-radius: 999px; padding: 2px 10px;
    font-weight: 700; font-size: 12px; font-variant-numeric: tabular-nums;
  }
  .wc-goal-ball { font-size: 11px; line-height: 1; }
  .wc-goal-flag {
    width: 18px; height: 12px; object-fit: cover; border-radius: 2px;
    background: #2a3540; flex: 0 0 auto;
  }
  .wc-goal-player { color: #e7edf3; display: inline-flex; align-items: center; gap: 6px; }
  .wc-no-goals { color: #8a97a5; }

  /* Half-time divider between first- and second-half goals */
  .wc-half-sep {
    display: flex; align-items: center; gap: 8px;
    color: #6b7785; font-size: 10px; font-weight: 700;
    text-transform: uppercase; letter-spacing: .6px;
    margin: 1px 0;
  }
  .wc-half-sep::before, .wc-half-sep::after {
    content: ""; flex: 1; height: 1px; background: #222c37;
  }

  @media (max-width: 600px) {
    .wc-arrow { display: none; }
    .wc-stage { display: none; }

    /* Scale the whole bar down on small screens. */
    .wc-header { padding: 5px 10px; gap: 9px; }
    .wc-logo { height: 26px; }
    .wc-logo-emoji { font-size: 20px; }
    .wc-title { font-size: 12px; letter-spacing: .6px; }
    .wc-bar { height: 46px; border-bottom-width: 1px; }
    .wc-pinned { gap: 5px; padding: 5px 8px; }
    .wc-scroller { gap: 5px; padding: 5px 8px; }

    .wc-card { gap: 6px; height: 32px; padding: 4px 8px; border-radius: 8px; }
    .wc-card.wc-live, .wc-card.wc-upcoming, .wc-card.wc-ft { border-left-width: 2px; }

    .wc-side { gap: 4px; font-size: 11px; }
    .wc-flag { width: 15px; height: 10px; }
    .wc-score { font-size: 12px; padding: 0 5px; }
    .wc-state { gap: 3px; font-size: 10px; }
    .wc-dot { width: 6px; height: 6px; }

    /* Detail dropdown a touch tighter too. */
    .wc-detail { padding: 10px 12px; font-size: 12px; gap: 8px; }
    .wc-detail-teams { font-size: 14px; }
    .wc-goal-flag { width: 15px; height: 10px; }
    .wc-goal-pill { font-size: 11px; padding: 2px 8px; }
  }

  @media (prefers-reduced-motion: reduce) {
    .wc-dot { animation: none; }
    .wc-scroller { scroll-behavior: auto; }
  }
  `;
}
